import React, { useState, useEffect } from 'react';
import { Search, Filter, X, ChevronDown, Calendar, User, Hash, IndianRupee, CreditCard, FileText } from 'lucide-react';

const todayStr = () => new Date().toISOString().split('T')[0];

export default function Filters({ initialValues = {}, onApply, onReset }) {
  const [inputs, setInputs] = useState(() => ({
    finalizedAt: todayStr(),
    ...initialValues,
  }));

  useEffect(() => {
    setInputs(initialValues);
  }, [initialValues]);

  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const cleanedInputs = Object.fromEntries(
      Object.entries(inputs).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    onApply(cleanedInputs);
  };

  const handleReset = () => {
    setInputs({ finalizedAt: todayStr() });
    if (onReset) onReset();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleApply();
  };

  const paymentStatusOptions = [
    { value: '', label: 'All Status' },
    // { value: 'paid', label: 'Paid' },
    // { value: 'pending', label: 'Pending' },
    // { value: 'partial', label: 'Partial' },
    { value: 'pending', label: 'Payment Pending' },
    { value: 'completed', label: 'Closed' },
  ];

  const paymentModeOptions = [
    { value: '', label: 'All Modes' },
    { value: 'CASH', label: 'Cash' },
    { value: 'UPI', label: 'UPI' },
  ];

  const ncOptions = [
    { value: '', label: 'All Bills' },
    { value: 'true', label: 'NC Only' },
    { value: 'false', label: 'Chargeable Only' },
  ];

  // const billStatusOptions = [
  //   { value: '', label: 'All Status' },
  //   { value: 'open', label: 'Open' },
  //   { value: 'closed', label: 'Closed' },
  // ];

  const amountOperators = [
    { value: '', label: 'Select...' },
    { value: '=', label: 'Equal to (=)' },
    { value: '>', label: 'Greater than (>)' },
    { value: '<', label: 'Less than (<)' },
    { value: '>=', label: 'Greater or equal (>=)' },
    { value: '<=', label: 'Less or equal (<=)' },
    { value: 'between', label: 'Between' },
  ];

  const activeFiltersCount = Object.values(inputs).filter(v => v !== '' && v !== null && v !== undefined).length;

  return (
    <div className="mb-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-50 to-green-50 px-5 py-3 border-b border-teal-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-teal-600" />
            <span className="font-semibold text-teal-800">Search & Filter</span>
            {activeFiltersCount > 0 && (
              <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Row 1: Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Bill Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Bill Number</label>
            <div className="relative">
              <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.billNumber || ''}
                onChange={e => updateInput('billNumber', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search bill..."
              />
            </div>
          </div>

          {/* Table Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Table Number</label>
            <div className="relative">
              <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.tableNumber || ''}
                onChange={e => updateInput('tableNumber', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Table no..."
              />
            </div>
          </div>

          {/* Customer Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Customer Name</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.customerName || ''}
                onChange={e => updateInput('customerName', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Customer name..."
              />
            </div>
          </div>

          {/* Created By */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Created By</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.createdBy || ''}
                onChange={e => updateInput('createdBy', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Username..."
              />
            </div>
          </div>
        </div>

        {/* Row 2: Status & Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Payment Status */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Payment Status</label>
            <div className="relative">
              <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="w-full pl-10 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none bg-gray-50 focus:bg-white cursor-pointer"
                value={inputs.paymentStatus || ''}
                onChange={e => updateInput('paymentStatus', e.target.value)}
              >
                {paymentStatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Bill Status */}
          {/* <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Bill Status</label>
            <div className="relative">
              <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="w-full pl-10 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none bg-gray-50 focus:bg-white cursor-pointer"
                value={inputs.billStatus || ''}
                onChange={e => updateInput('billStatus', e.target.value)}
              >
                {billStatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div> */}

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Payment Mode</label>
            <div className="relative">
              <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="w-full pl-10 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none bg-gray-50 focus:bg-white cursor-pointer"
                value={inputs.paymentMode || ''}
                onChange={e => updateInput('paymentMode', e.target.value)}
              >
                {paymentModeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* NC Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">NC Bills</label>
            <div className="relative">
              <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="w-full pl-10 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none bg-gray-50 focus:bg-white cursor-pointer"
                value={inputs.isNc || ''}
                onChange={e => updateInput('isNc', e.target.value)}
              >
                {ncOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Date Presets */}
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Quick Date</label>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const t = new Date(); const tStr = t.toISOString().split('T')[0];
                const y = new Date(); y.setDate(y.getDate()-1); const yStr = y.toISOString().split('T')[0];
                const w = new Date(); w.setDate(w.getDate()-6); const wStr = w.toISOString().split('T')[0];
                const m = new Date(); m.setDate(m.getDate()-29); const mStr = m.toISOString().split('T')[0];
                const start = inputs.finalizedAt || ''; const end = inputs.endDate || '';
                const presets = [
                  { label: 'Today', start: tStr, end: '', isActive: start === tStr && !end },
                  { label: 'Yesterday', start: yStr, end: '', isActive: start === yStr && !end },
                  { label: 'Last 7 Days', start: wStr, end: tStr, isActive: start === wStr && end === tStr },
                  { label: 'Last 30 Days', start: mStr, end: tStr, isActive: start === mStr && end === tStr },
                ];
                return presets.map(p => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => { updateInput('finalizedAt', p.start); updateInput('endDate', p.end); }}
                    className={`px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                      p.isActive
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'border-gray-200 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ));
              })()}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">From Date</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.finalizedAt || ''}
                onChange={e => updateInput('finalizedAt', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">To Date</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50 focus:bg-white"
                value={inputs.endDate || ''}
                onChange={e => updateInput('endDate', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Amount Filter - Highlighted Section */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <IndianRupee size={18} className="text-amber-600" />
            <span className="font-semibold text-amber-800">Amount Filter</span>
            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">Filter bills by amount</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Operator Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Condition</label>
              <select
                className="w-full px-3 py-2.5 text-sm border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-white cursor-pointer"
                value={inputs.finalAmountOp || ''}
                onChange={e => {
                  updateInput('finalAmountOp', e.target.value);
                  if (e.target.value !== 'between') {
                    updateInput('finalAmountValue2', '');
                  }
                }}
              >
                {amountOperators.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Amount Value 1 */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {inputs.finalAmountOp === 'between' ? 'Min Amount (₹)' : 'Amount (₹)'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-3 py-2.5 text-sm border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-white"
                value={inputs.finalAmountValue || ''}
                onChange={e => updateInput('finalAmountValue', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={inputs.finalAmountOp === 'between' ? 'Min amount...' : 'Enter amount...'}
                disabled={!inputs.finalAmountOp}
              />
            </div>

            {/* Amount Value 2 (for between) */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Max Amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`w-full px-3 py-2.5 text-sm border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all ${
                  inputs.finalAmountOp === 'between' ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                }`}
                value={inputs.finalAmountValue2 || ''}
                onChange={e => updateInput('finalAmountValue2', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Max amount..."
                disabled={inputs.finalAmountOp !== 'between'}
              />
            </div>
          </div>

          {/* Quick Amount Presets */}
          {inputs.finalAmountOp && inputs.finalAmountOp !== 'between' && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs text-amber-700 mr-2 self-center">Quick:</span>
              {[500, 1000, 2000, 5000, 10000].map(amt => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => updateInput('finalAmountValue', amt.toString())}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                    inputs.finalAmountValue === amt.toString()
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  ₹{amt.toLocaleString()}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-gray-500">
            {activeFiltersCount > 0 && `${activeFiltersCount} filter(s) applied`}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={16} />
              Clear All
            </button>
            <button
              onClick={handleApply}
              className="flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm transition-all"
            >
              <Search size={16} />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
